import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SmartBanner from 'react-smartbanner';
import useScreenType from '../../hooks/context/useScreenType';
import useBrand from '../../hooks/context/useBrand';
import APP_STORE_IDS from '../../services/branding/appStoreIds.json';
import 'react-smartbanner/dist/main.css';
import { ENVIRONMENTS } from '../../services/exports/Constants';

// react-smartbanner takes no app id as a prop. `parseAppId()` reads it out of
// `meta[name="apple-itunes-app"]` / `meta[name="google-play-app"]` during its own
// mount, and `render()` returns an empty <div> while `state.appId` is empty — it
// never reaches the point where a `url` prop would be used. So the metas are the
// banner's data source, not a second banner beside it.
//
// They used to sit in index.html, where Safari built its own Smart App Banner
// from them above a partner's branded dashboard, and no runtime brand check
// could retract it. They live here now: written only once every gate below
// agrees the banner belongs on this page, and taken out again if one closes.
//
// This component draws the banner for Android and for non-Safari iOS. iPhone
// Safari is served by Safari itself, from an `apple-itunes-app` meta that has to
// be in the HTML at parse time — Safari ignores one inserted later, so nothing
// here can reach it. That meta ships in a separate shell built by
// `scripts/build-foodamigos-shell.mjs` and served only on the FoodAmigos
// production host, which is why removing it from index.html did not have to
// mean giving up the iOS banner. `setType()` deliberately falls through on
// Mobile Safari for exactly this reason: it is leaving the field to Safari.
const APP_ID_METAS = {
  'apple-itunes-app': `app-id=${APP_STORE_IDS.ios}`,
  'google-play-app': `app-id=${APP_STORE_IDS.android}`,
};

// Returns true once the metas are in the DOM. SmartBanner parses them while it
// mounts, so it can only be rendered on the commit after they were written.
function useAppIdMetas(enabled) {
  const [present, setPresent] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const metas = Object.entries(APP_ID_METAS).map(([name, content]) => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);

      return meta;
    });

    setPresent(true);

    return () => {
      metas.forEach((meta) => meta.remove());
      setPresent(false);
    };
  }, [enabled]);

  return present;
}

export default function SmartAppBanner() {
  const { isDesktop } = useScreenType();

  const { isMobileApp } = useSelector((state) => state.appStatus);
  const brand = useBrand();

  // The banner advertises the FoodAmigos store app — never on a partner brand.
  const belongsHere =
    !brand?.isWhiteLabel && !isDesktop && import.meta.env.VITE_APP_MODE === ENVIRONMENTS.PROD && !isMobileApp;

  const hasAppIds = useAppIdMetas(belongsHere);

  return belongsHere && hasAppIds && <SmartBanner title="FoodAmigos-Dashboard" position="bottom" />;
}
